<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useWizardForms } from '@/composables/useWizardForms'
import { useWizardPages } from '@/composables/useWizardPages'
import type { WizardPage } from '@/types/wizard-forms'
import WizardFormDetailsForm from '@/components/organizer/wizard-forms/WizardFormDetailsForm.vue'
import WizardPageList from '@/components/organizer/wizard-forms/WizardPageList.vue'
import WizardPageEditor from '@/components/organizer/wizard-forms/WizardPageEditor.vue'
import WizardPageFixedPreview from '@/components/organizer/wizard-forms/WizardPageFixedPreview.vue'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'

function isAxiosError(
  e: unknown,
): e is { response: { status: number; data: { errors?: Record<string, string[]>; message?: string } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

const { t } = useI18n()
const route = useRoute()
const { form, loading: formLoading, error: formError, fetchForm, updateForm } = useWizardForms()
const { pages, loading: pagesLoading, error: pagesError, fetchPages, createPage, deletePage, reorderPages } = useWizardPages()

const formId = Number(route.params['formId'])

const activePageId = ref<number | null>(null)
const showFixedPage = ref(true)
const activePage = computed<WizardPage | null>(
  () => pages.value.find((p) => p.id === activePageId.value) ?? null,
)

// Details form state
const detailsLoading = ref(false)
const detailsErrors = ref<Record<string, string[]>>({})
const detailsError = ref('')

// Add page
const addingPage = ref(false)

onMounted(async () => {
  await Promise.all([fetchForm(formId), fetchPages(formId)])
})

async function handleDetailsSubmit(payload: { name: string; description: string | null }): Promise<void> {
  detailsErrors.value = {}
  detailsError.value = ''
  detailsLoading.value = true
  try {
    await updateForm(formId, payload)
  } catch (e: unknown) {
    if (isAxiosError(e) && e.response.status === 422 && e.response.data.errors) {
      detailsErrors.value = e.response.data.errors
    } else {
      detailsError.value = t('common.error_generic')
    }
  } finally {
    detailsLoading.value = false
  }
}

async function handleAddPage(): Promise<void> {
  addingPage.value = true
  try {
    const newPage = await createPage(formId, {
      title: t('wizard_forms.pages.new_page_title'),
    })
    activePageId.value = newPage.id
    showFixedPage.value = false
  } finally {
    addingPage.value = false
  }
}

async function handleDeletePage(pageId: number): Promise<void> {
  await deletePage(formId, pageId)
  if (activePageId.value === pageId) {
    activePageId.value = null
    showFixedPage.value = true
  }
}

async function handleReorderPages(pageIds: number[]): Promise<void> {
  await reorderPages(formId, pageIds)
}
</script>

<template>
  <OrganizerLayout>

    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="mb-6 flex items-center justify-between">
        <RouterLink
          to="/organizer/wizard-forms"
          class="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1"
        >

          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          {{ t('wizard_forms.form.back_to_list') }}
        </RouterLink>
      </div>

      <!-- Page loading -->
      <AppSpinner v-if="formLoading" />

      <div v-else-if="formError" class="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400">
        {{ t(formError) }}
      </div>

      <template v-else-if="form">
        <h1 class="text-3xl font-bold text-white mb-8">{{ form.name }}</h1>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <!-- Left: Form details + pages sidebar -->
          <div class="space-y-6">
            <!-- Form details -->
            <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-5">
              <h2 class="text-sm font-semibold text-white mb-4">{{ t('wizard_forms.form.edit_heading') }}</h2>

              <div
                v-if="detailsError"
                class="mb-4 rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2.5 text-xs text-red-400"
              >
                {{ detailsError }}
              </div>

              <WizardFormDetailsForm
                :initial-data="{ name: form.name, description: form.description }"
                :loading="detailsLoading"
                :errors="detailsErrors"
                @submit="handleDetailsSubmit"
              />
            </div>

            <!-- Pages sidebar -->
            <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-5">
              <h2 class="text-sm font-semibold text-white mb-4">{{ t('wizard_forms.pages.heading') }}</h2>

              <div v-if="pagesLoading" class="flex items-center justify-center py-6">
                <svg class="animate-spin h-5 w-5 text-emerald-500" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>

              <div
                v-else-if="pagesError"
                class="rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2.5 text-xs text-red-400"
              >
                {{ t(pagesError) }}
              </div>

              <WizardPageList
                v-else
                :pages="pages"
                :active-page-id="activePageId"
                :fixed-page-active="showFixedPage"
                @select="(id) => { activePageId = id; showFixedPage = false }"
                @select-fixed="() => { activePageId = null; showFixedPage = true }"
                @reorder="handleReorderPages"
                @add="handleAddPage"
                @delete="handleDeletePage"
              />
            </div>
          </div>

          <!-- Right: Page editor -->
          <div class="lg:col-span-2">
            <!-- Fixed page 1 preview -->
            <WizardPageFixedPreview v-if="showFixedPage" />

            <!-- Custom page editor -->
            <div
              v-else-if="activePage"
              class="bg-slate-800/50 border border-slate-700 rounded-2xl p-5"
            >
              <WizardPageEditor
                :key="activePage.id"
                :page="activePage"
                :form-id="formId"
                @updated="() => fetchPages(formId)"
                @field-added="() => {}"
                @field-updated="() => {}"
                @field-deleted="() => {}"
              />
            </div>

            <div
              v-else
              class="rounded-2xl border border-dashed border-slate-700 bg-slate-800/30 px-8 py-16 text-center"
            >
              <p class="text-slate-400 text-sm mb-4">{{ t('wizard_forms.pages.no_pages') }}</p>
              <button
                type="button"
                :disabled="addingPage"
                class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white font-semibold rounded-lg px-4 py-2.5 text-sm transition-colors"
                @click="handleAddPage"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
                {{ t('wizard_forms.pages.add_page') }}
              </button>
            </div>
          </div>
        </div>
      </template>
    </main>
  </OrganizerLayout>
</template>
